import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
}

interface UserTenant {
  tenant_id: string;
  role: 'admin' | 'manager' | 'instructor' | 'warden' | 'user' | 'customer' | 'super_admin';
  tenants: Tenant;
}

interface TenantContextType {
  activeTenant: Tenant | null;
  userTenants: UserTenant[];
  allTenants: Tenant[];
  isLoading: boolean;
  isSuperAdmin: boolean;
  switchTenant: (tenantId: string) => void;
  refreshTenants: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
};

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeTenant, setActiveTenant] = useState<Tenant | null>(null);
  const [userTenants, setUserTenants] = useState<UserTenant[]>([]);
  const [allTenants, setAllTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const navigate = useNavigate();

  const refreshTenants = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setIsLoading(false);
        return;
      }

      // Check if user is super admin
      const { data: superAdminRows, error: superAdminError } = await supabase
        .from('user_tenants')
        .select('role')
        .eq('user_id', session.user.id)
        .eq('role', 'super_admin');

      if (superAdminError) throw superAdminError;

      const isSuperAdminUser = !!superAdminRows && superAdminRows.length > 0;
      setIsSuperAdmin(isSuperAdminUser);

      // Fetch user's tenants
      const { data: tenantsData, error } = await supabase
        .from('user_tenants')
        .select(`
          tenant_id,
          role,
          tenants (
            id,
            name,
            slug,
            is_active
          )
        `)
        .eq('user_id', session.user.id)
        .order('role', { ascending: true });

      if (error) throw error;

      const validTenants = (tenantsData || []).filter(
        (ut): ut is UserTenant => ut.tenants !== null
      );

      setUserTenants(validTenants);

      // Fetch all active tenants for customers to browse
      const { data: allTenantsData, error: allTenantsError } = await supabase
        .from('tenants')
        .select('id, name, slug, is_active')
        .eq('is_active', true)
        .order('name');

      if (allTenantsError) throw allTenantsError;
      setAllTenants(allTenantsData || []);

      // Set active tenant from localStorage or default to first tenant
      const storedTenantId = localStorage.getItem('activeTenantId');
      const tenant = validTenants.find(ut => ut.tenant_id === storedTenantId)?.tenants 
        || validTenants[0]?.tenants
        || allTenantsData?.[0];

      if (tenant) {
        setActiveTenant(tenant);
        localStorage.setItem('activeTenantId', tenant.id);
      }
    } catch (error) {
      console.error('Error loading tenants:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshTenants();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        refreshTenants();
      } else if (event === 'SIGNED_OUT') {
        setActiveTenant(null);
        setUserTenants([]);
        setIsSuperAdmin(false);
        localStorage.removeItem('activeTenantId');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const switchTenant = (tenantId: string) => {
    const tenant = userTenants.find(ut => ut.tenant_id === tenantId)?.tenants 
      || allTenants.find(t => t.id === tenantId);
    if (tenant) {
      setActiveTenant(tenant);
      localStorage.setItem('activeTenantId', tenant.id);
      // Don't redirect for customers, just update the active tenant
      if (isSuperAdmin) {
        navigate('/dashboard');
      }
    }
  };

  return (
    <TenantContext.Provider
      value={{
        activeTenant,
        userTenants,
        allTenants,
        isLoading,
        isSuperAdmin,
        switchTenant,
        refreshTenants,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};
